import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
  Collapse,
  Chip,
  Stack,
  InputAdornment
} from '@mui/material'
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface SearchBarProps {
  cities: string[]
  value: { q: string; city: string; min?: number; max?: number; furnished: boolean }
  onChange: (value: any) => void
  onSubmit: () => void
  onReset?: () => void
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange, onSubmit, onReset }) => {
  const { t } = useTranslation()
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  const handleClearFilters = () => {
    if (onReset) { onReset(); return }
    onChange({
      q: '',
      city: '',
      min: undefined,
      max: undefined,
      furnished: false
    })
  }

  const hasActiveFilters = value.q || value.city || value.min != null || value.max != null || value.furnished

  const popularCities = [
    'Berlin', 'München', 'Hamburg', 'Köln', 'Frankfurt', 'Stuttgart',
    'Düsseldorf', 'Dortmund', 'Essen', 'Leipzig', 'Bremen', 'Dresden'
  ]

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        {t('header.subtitle')}
      </Typography>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
        {/* Main Search Row */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('search.location')}
              value={value.city}
              onChange={(e) => onChange({ ...value, city: e.target.value })}
              placeholder={t('header.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="room-type-label">{t('search.roomType')}</InputLabel>
              <Select
                labelId="room-type-label"
                value={value.q}
                onChange={(e) => onChange({ ...value, q: e.target.value })}
                label={t('search.roomType')}
              >
                <MenuItem value="">{t('search.any')}</MenuItem>
                <MenuItem value="wg-zimmer">WG-Zimmer</MenuItem>
                <MenuItem value="1-zimmer-wohnung">1-Zimmer-Wohnung</MenuItem>
                <MenuItem value="wohnung">Wohnung</MenuItem>
                <MenuItem value="haus">Haus</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('search.maxPrice')}
              type="number"
              value={value.max ?? ''}
              onChange={(e) => onChange({ ...value, max: e.target.value ? Number(e.target.value) : undefined })}
              placeholder={t('search.maxPricePlaceholder') as string}
              InputProps={{
                endAdornment: <Typography sx={{ color: 'text.secondary' }}>€</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SearchIcon />}
              sx={{ height: '56px', fontWeight: 'bold' }}
            >
              {t('header.findOffers')}
            </Button>
          </Grid>
        </Grid>

        {/* Active Filters */}
        {hasActiveFilters && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {value.city && (
                <Chip
                  label={`${t('search.location')}: ${value.city}`}
                  onDelete={() => onChange({ ...value, city: '' })}
                  color="primary"
                  variant="outlined"
                />
              )}
              {value.max != null && (
                <Chip
                  label={`${t('search.maxPrice')}: ${value.max}€`}
                  onDelete={() => onChange({ ...value, max: undefined })}
                  color="primary"
                  variant="outlined"
                />
              )}
              {value.min != null && (
                <Chip
                  label={`${t('search.minSize')}: ${value.min}m²`}
                  onDelete={() => onChange({ ...value, min: undefined })}
                  color="primary"
                  variant="outlined"
                />
              )}
              {value.furnished && (
                <Chip
                  label={t('search.furnished')}
                  onDelete={() => onChange({ ...value, furnished: false })}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip
                icon={<ClearIcon />}
                label={t('search.clearAllFilters')}
                onClick={handleClearFilters}
                color="default"
                variant="outlined"
              />
            </Stack>
          </Box>
        )}

        {/* Advanced Filters Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<FilterIcon />}
            endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            sx={{ textTransform: 'none' }}
          >
            {t('search.moreFilters')}
          </Button>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showAdvancedFilters}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('search.minSize')}
                type="number"
                value={value.min ?? ''}
                onChange={(e) => onChange({ ...value, min: e.target.value ? Number(e.target.value) : undefined })}
                placeholder={t('search.minSizePlaceholder') as string}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('search.availableFrom')}
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('search.wgSize')}</InputLabel>
                <Select
                  label={t('search.wgSize') as string}
                  value=""
                  onChange={() => { }} // Placeholder - not implemented yet
                >
                  <MenuItem value="">{t('search.any')}</MenuItem>
                  <MenuItem value="2">2er WG</MenuItem>
                  <MenuItem value="3">3er WG</MenuItem>
                  <MenuItem value="4">4er WG</MenuItem>
                  <MenuItem value="5">5er WG</MenuItem>
                  <MenuItem value="6+">6+ WG</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.furnished}
                    onChange={(e) => onChange({ ...value, furnished: e.target.checked })}
                  />
                }
                label={t('search.furnished')}
              />
            </Grid>
          </Grid>
        </Collapse>

        {/* Popular Cities */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            {t('search.popularCitiesLabel')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {popularCities.map((city) => (
              <Chip
                key={city}
                label={city}
                onClick={() => onChange({ ...value, city })}
                variant={value.city === city ? 'filled' : 'outlined'}
                color={value.city === city ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Paper>
  )
}

export default SearchBar
